/**
 * @module frontend/theme/layers
 * @description The stacking scale every layered surface orders itself against
 *
 * One module owns the scale so a portalled surface cannot pick a number that
 * disagrees with the one a sibling picked, and so a stylesheet can read the
 * same numbers back as custom properties rather than restating them.
 *
 * @exports LAYER - The ordered roles, as complete Tailwind z-index utilities
 * @exports layerZIndex - The one parser of those utilities
 * @exports layerVariables - The `--layer-*` emission for the rendered theme
 *
 * Imports nothing, so `scripts/emitTheme.ts` can load it under Node.
 */

/**
 * Ordered stacking scale for portalled/fixed surfaces, as COMPLETE Tailwind
 * z-index class strings: the JIT only emits a rule for a class it can scan
 * verbatim, so a utility assembled by interpolation reaches the DOM with no
 * stylesheet behind it.
 *
 * Stacking order has exactly one owner here so layered surfaces cannot drift
 * into covering each other: before this scale existed, the modal overlay and
 * the Radix select popup each carried their own independent z-index literal,
 * and raising one without the other silently buried a dropdown's option list
 * under a dialog (the todo-modal-select defect). `float` and `tooltip` widen
 * the scale to the two roles that were still on unowned literals: a fixed
 * floating control that sits above page content but below a drawer, and a
 * transient hint that must clear every other layered role including a popover.
 *
 * @constant LAYER
 */
export const LAYER = {
  // The rail's paired backdrop: under the rail, over page content.
  scrim: 'z-20',
  float: 'z-30',
  // The account disclosure: above the rail, under every drawer.
  railPopover: 'z-[35]',
  drawer: 'z-40',
  overlay: 'z-50',
  modal: 'z-[60]',
  popover: 'z-[70]',
  tooltip: 'z-[80]',
} as const

/**
 * Layer name type definition
 */
export type LayerName = keyof typeof LAYER

/**
 * A complete Tailwind z-index utility: the bracketed arbitrary form (`z-[60]`)
 * or the bare numeric scale (`z-40`). The JIT only emits a rule for a class it
 * can scan verbatim, so anything else compiles to nothing.
 */
const Z_INDEX_UTILITY = /^z-(?:\[(\d+)\]|(\d+))$/

/**
 * Number a complete Tailwind z-index utility names (`z-40` → 40, `z-[60]` → 60).
 *
 * The one parser the layer fence, the browser paint-order suite and the theme's
 * variable emission share, so none of them can disagree with the scale about
 * what a role outranks.
 */
export function layerZIndex(utility: string): number {
  const match = Z_INDEX_UTILITY.exec(utility)
  if (!match) {
    throw new Error(`not a complete Tailwind z-index utility: ${utility}`)
  }
  return Number(match[1] ?? match[2])
}

/**
 * Kebab form of a camelCase role, because CSS custom properties are
 * case-sensitive and `--layer-railPopover` is not the name a stylesheet reads.
 */
type KebabCase<S extends string> = S extends `${infer Head}${infer Tail}`
  ? Head extends Uppercase<Head>
    ? Head extends Lowercase<Head>
      ? `${Head}${KebabCase<Tail>}`
      : `-${Lowercase<Head>}${KebabCase<Tail>}`
    : `${Head}${KebabCase<Tail>}`
  : S

/** The custom property a layer role is emitted as. */
export type LayerVariableName = `--layer-${KebabCase<LayerName>}`

/**
 * `{ '--layer-scrim': '20', '--layer-rail-popover': '35', … }`, rendered into
 * `:root` by `scripts/emitTheme.ts` (`src/theme/tokens.css`) so a stylesheet
 * can order itself against the scale without restating one of its numbers.
 */
export function layerVariables(): Record<LayerVariableName, string> {
  const declarations = {} as Record<LayerVariableName, string>
  for (const role of Object.keys(LAYER) as readonly LayerName[]) {
    // The runtime kebab conversion produces exactly what KebabCase computes at
    // the type level, which TypeScript cannot follow through a string replace.
    const variable = `--layer-${role.replace(/[A-Z]/g, (letter) => `-${letter.toLowerCase()}`)}` as LayerVariableName
    declarations[variable] = String(layerZIndex(LAYER[role]))
  }
  return declarations
}
